// frontend/src/pages/supplierDeleteImpact.js
//
// The confirmation text for a bulk supplier delete.
//
// Deleting a supplier is not contained: purchase orders, purchase order
// recommendations and item links all name it with
// on_delete=CASCADE, and nothing is PROTECT. A merchant clearing 300 rows off
// the Suppliers grid is therefore also deleting order history. This module
// turns the server's blast radius into the sentence that says it.

// Merchant-facing noun for each kind of row that dies alongside a supplier,
// keyed by Django model label — what the endpoint returns, because it derives
// the casualties from the model graph rather than a hand-written list, so a
// new CASCADE relation appears without anyone remembering to add it.
//
// The label set is pinned by api/test_supplier_bulk_delete.py; that test fails
// when a relation is added, which is the prompt to add its noun to all four
// locale files. The noun set is pinned in turn by audit-i18n.config.json.
//
// `purchase_orders_calculations` is the app LABEL, which differs from the
// package name (see backend/purchase_orders/apps.py).
export const CASCADE_NOUNS = {
  'purchase_orders_calculations.purchaseorderheader': 'purchaseOrders',
  'purchase_orders_calculations.porecommendation': 'poRecommendations',
  'api.suppliercatalogitem': 'catalogLinks',
};

/**
 * The confirmation message for a pending bulk delete.
 *
 * THROWS rather than degrading. Both failure modes below mean the merchant
 * would be shown a blast radius that understates the real one, and this
 * dialog's only job is to be complete — a delete nobody can describe is a
 * delete that must not proceed. The caller turns the throw into a refusal.
 *
 * @param {{suppliers: number, impact: Object<string, number>}} preview
 *   The gate response — what the delete WOULD destroy, computed server-side
 *   with nothing mutated yet. `impact` is always present; `{}` means the
 *   delete is contained.
 * @param {Function} t  i18next translator (SaaS instance).
 * @returns {string} <ConfirmDialog> markup: `\n\n` splits paragraphs, `• `
 *   lines become a bullet list.
 */
export function describeDelete(preview, t) {
  if (!preview.impact || typeof preview.impact !== 'object') {
    // Not "assume nothing cascades" — a response missing `impact` is a
    // malformed gate, and treating it as a contained delete would hide
    // purchase orders behind a reassuring dialog.
    throw new Error('bulk-delete gate returned no impact');
  }

  const casualties = Object.entries(preview.impact).map(([label, count]) => {
    const noun = CASCADE_NOUNS[label];
    if (!noun) {
      // A CASCADE relation reached the wire without a noun — both ratchets
      // (the backend label pin and audit-i18n's required noun set) were
      // bypassed. Rendering the raw label would put an untranslated string in
      // front of a merchant and still leave them unable to judge the delete.
      throw new Error(`bulk-delete gate reported an unnamed casualty: ${label}`);
    }
    return t(`supplierSetup.messages.deleteCascade.${noun}`, { count });
  });

  return [
    t('supplierSetup.messages.deleteSummary', { count: preview.suppliers }),
    casualties.length
      ? [t('supplierSetup.messages.deleteCascadeIntro'),
         ...casualties.map((line) => `• ${line}`)].join('\n')
      : null,
    t('supplierSetup.messages.deleteIrreversible'),
  ].filter(Boolean).join('\n\n');
}
